/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useState, useEffect, useRef, useMemo } from 'react';
import DataAdminServiceFactory from '../../../services/DataAdminServiceFactory';
import { logEvent, logError, logTrace } from '../../../utils/trackingUtils';
import { CaseProcedureAddDTO, CaseDetail } from '../../../models/DataAdminTypes';
import { getUniqueCaseIds, sortAlphaNumericStrings } from '../../../utils/caseUtils';
import { ProcedureRowData } from './ProcedureRow';
import { NewCaseFolderModalProps } from './NewCaseFolderModal';

// You can also define the subset of props needed if you prefer, 
// but using the main Props type is fine for the hook input.
export const useCaseModalLogic = ({ site, getAccessToken, env, onClose }: NewCaseFolderModalProps) => {
  // --- Workflow State ---
  const [step, setStep] = useState<number>(1);

  // --- Triage State ---
  const [isStaged, setIsStaged] = useState<boolean | null>(null);
  const [hasPrevious, setHasPrevious] = useState<boolean | null>(null);
  const [parentCaseId, setParentCaseId] = useState<string>('');
  const [existingCases, setExistingCases] = useState<string[]>([]);

  // --- Form Data State ---
  const [caseId, setCaseId] = useState('');
  const [date, setDate] = useState('');
  const [isPrimary, setIsPrimary] = useState<boolean | null>(null);
  const [isNavigated, setIsNavigated] = useState<boolean | null>(null);
  const [procedureRowId, setProcedureRowId] = useState<number>(1);
  const [procedures, setProcedures] = useState<ProcedureRowData[]>([
    { id: '1', startLevel: '', endLevel: '', procedureType: '', isValid: null }
  ]);

  // --- UI/Validation State ---
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [procedureError, setProcedureError] = useState<string | null>(null);
  const [caseIdError, setCaseIdError] = useState<string | null>(null);
  const [isCreated, setIsCreated] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [newFolderName, setNewFolderName] = useState<string | null>(null);

  // FIX: Removed '| null' from generic. This creates a standard RefObject<HTMLFormElement>.
  const formRef = useRef<HTMLFormElement>(null);

  // Memoize the service so it doesn't recreate on every render
  const dataAdminService = useMemo(() => new DataAdminServiceFactory().create(env), [env]);

  // --- EFFECTS ---

  // 1. Fetch Staged Cases
  useEffect(() => {
    const fetchStagedData = async () => {
      if (isStaged === true && hasPrevious === true) {
        try {
          const token = await getAccessToken();
          const results: CaseDetail[] = await dataAdminService.getCaseDetailsForSite(token, site.siteCode, true);
          setExistingCases(sortAlphaNumericStrings(getUniqueCaseIds(results)));
        } catch (error) {
          console.error('❌ API ERROR:', error);
          setErrorMessage('Failed to load existing case list.');
        }
      }
    };
    fetchStagedData();
  }, [isStaged, hasPrevious, site.siteCode, getAccessToken, dataAdminService]);

  // 1b. Clear Case ID when not staged, or staged without previous
  useEffect(() => {
    if (isStaged === false || (isStaged === true && hasPrevious === false)) {
      setCaseId('');
    }
  }, [isStaged, hasPrevious]);

  // 2. Validate Form
  useEffect(() => {
    const validate = () => {
      // Validate Case ID
      const trimmedId = caseId.trim();
      const numId = Number(trimmedId);
      let idErr = null;

      const isLockedFromTriage = isStaged === true && hasPrevious === true;

      if (!isLockedFromTriage) {
        if (trimmedId.includes(' ')) {
          idErr = 'Case ID cannot contain spaces.';
        } else if (!Number.isInteger(numId) || numId <= 0) {
          if (trimmedId !== '') idErr = 'Case ID must be a number.';
        }
      } else {
        if (trimmedId === '') {
          idErr = 'Case ID is required.';
        }
      }
      setCaseIdError(idErr);
      const isIdValid = trimmedId !== '' && idErr === null;

      // Validate Procedures
      const rowsValid = procedures.every(row => row.isValid);
      const uniqueKeys = new Set(procedures.map(p => `${p.startLevel}-${p.endLevel}-${p.procedureType}`));
      const uniqueValid = uniqueKeys.size === procedures.length;
      setProcedureError(uniqueValid ? null : 'Duplicate procedure entries are not allowed.');

      return (
        isIdValid &&
        date !== '' &&
        isPrimary !== null &&
        isNavigated !== null &&
        rowsValid && uniqueValid
      );
    };

    setIsFormValid(validate());
  }, [caseId, date, procedures, isPrimary, isNavigated, isStaged, hasPrevious]);

  // --- HANDLERS ---

  const handleNext = () => {
    if (isStaged && hasPrevious && parentCaseId) {
      setCaseId(parentCaseId);
    }
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setErrorMessage(null);
  };

  // Procedure Row Logic
  const updateProcedure = (id: string, field: keyof Omit<ProcedureRowData, 'id'>, value: string) => {
    if (id === 'add') {
      const newId = procedureRowId + 1;
      setProcedureRowId(newId);
      setProcedures(prev => [...prev, { id: newId.toString(), startLevel: '', endLevel: '', procedureType: '', isValid: false }]);
    } else {
      setProcedures(prev => prev.map(row => {
        if (row.id === id) {
          const updated = { ...row, [field]: value };
          updated.isValid = updated.startLevel !== '' && updated.endLevel !== '' && updated.procedureType !== '';
          return updated;
        }
        return row;
      }));
    }
  };

  const removeProcedure = (id: string) => {
    setProcedures(prev => prev.filter(row => row.id !== id));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        caseId: caseId.trim(),
        caseDate: date,
        isRevision: isPrimary === false,
        isNavigated: isNavigated === true,
        isStaged: isStaged === true,
        procedures: procedures.map(p => ({
          locusStart: p.startLevel,
          locusEnd: p.endLevel,
          procedureType: p.procedureType
        } as CaseProcedureAddDTO))
      };

      const result = await dataAdminService.createCaseDetail(await getAccessToken(), site.siteCode, payload);
      setNewFolderName(result.caseFolder.replace(/\/$/, ''));
      setIsCreated(true);
      setErrorMessage(null);
      logEvent({
        action: 'case.create.success',
        siteCode: site.siteCode,
        caseId: payload.caseId,
        caseDate: payload.caseDate,
        isStaged: payload.isStaged,
        isNavigated: payload.isNavigated,
        isPrimary: isPrimary,
        procedureCount: procedures.length
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.log(err);
      setErrorMessage(message || 'An unexpected error occurred.');
      logError('case.create.error', err);
    } finally {
      setSubmitting(false);
    }
  };

  const isTriageValid =
    (isStaged === false) ||
    (isStaged === true && hasPrevious === false) ||
    (isStaged === true && hasPrevious === true && parentCaseId !== '');

  const handleClose = (folder: string | null) => {
    if (folder === null) {
      logEvent({ action: 'case.create.cancelled', siteCode: site.siteCode });
      logTrace('CaseCreate', 'case.create.cancelled', site.siteCode);
    }
    onClose(folder);
  };

  return {
    state: {
      step, isCreated, submitting, newFolderName, errorMessage,
      isStaged, hasPrevious, parentCaseId, existingCases,
      caseId, date, isPrimary, isNavigated, procedures,
      caseIdError, procedureError, isFormValid, isTriageValid
    },
    actions: {
      setStep, handleNext, handleBack, handleSubmit, onClose: handleClose,
      setIsStaged, setHasPrevious, setParentCaseId,
      setCaseId, setDate, setIsPrimary, setIsNavigated,
      updateProcedure, removeProcedure
    },
    refs: { formRef }
  };
};
